import { Icon } from "./Icon.jsx";

// ─── CATEGORY ART ────────────────────────────────────────────────────────────
// The "Shop by category" tiles: the category's line icon stays exactly as it was, and behind it sits a small
// illustration of what that category actually is (gems and sparkles for Jewellery, a hanger and tee for Apparel…)
// on its own translucent colour. Each illustration is drawn in a darker shade of its tile's hue, low-contrast, so the
// icon in front is still the thing you read. Pure inline SVG — no image downloads, scales to any tile size.
export const CATEGORY_HUES = {
  jewellery:    { bg: "rgba(255,194,31,0.30)",  ink: "#B7791F" },
  hair:         { bg: "rgba(240,120,150,0.24)", ink: "#B03A57" },
  charms:       { bg: "rgba(70,185,235,0.24)",  ink: "#1F7BAE" },
  apparel:      { bg: "rgba(245,145,55,0.27)",  ink: "#B5600D" },
  bags:         { bg: "rgba(196,146,90,0.30)",  ink: "#855522" },
  collectibles: { bg: "rgba(55,172,172,0.24)",  ink: "#1E7A7A" },
  home:         { bg: "rgba(105,145,225,0.24)", ink: "#35589E" },
  beauty:       { bg: "rgba(240,100,80,0.24)",  ink: "#B0321D" },
  bundles:      { bg: "rgba(255,160,70,0.28)",  ink: "#B5600D" },
};

const sw = { fill: "none", stroke: "currentColor", strokeWidth: 1.5, strokeLinecap: "round", strokeLinejoin: "round" };
const fillSoft = { fill: "currentColor", fillOpacity: 0.14, stroke: "currentColor", strokeWidth: 1.4, strokeLinejoin: "round" };
const Sparkle = ({ x, y, r = 4 }) => <path d={`M${x} ${y - r}L${x + r * 0.3} ${y - r * 0.3}L${x + r} ${y}L${x + r * 0.3} ${y + r * 0.3}L${x} ${y + r}L${x - r * 0.3} ${y + r * 0.3}L${x - r} ${y}L${x - r * 0.3} ${y - r * 0.3}Z`} fill="currentColor" fillOpacity="0.55" />;

// 72 × 72 drawings; they bleed off the tile edges on purpose, like a pattern behind the icon.
const ART = {
  // loose gems, a ring with a stone, sparkles
  jewellery: (
    <>
      <path {...fillSoft} d="M6 12l6-6h8l6 6-10 12z" />
      <path {...sw} d="M6 12h20M12 6l4 18M20 6l-4 18" />
      <circle {...sw} cx="54" cy="52" r="11" /><path {...fillSoft} d="M48 38l6-6 6 6-6 7z" />
      <path {...fillSoft} d="M58 10l5 5-5 6-5-6z" />
      <Sparkle x="14" y="52" r="5" /><Sparkle x="40" y="14" r="3" /><Sparkle x="66" y="32" r="3" />
    </>
  ),
  // bows, a barrette, flowing strands
  hair: (
    <>
      <path {...fillSoft} d="M8 14c6-8 14-2 14 4s-8 12-14 4zM36 18c-6-8-14-2-14 4" />
      <circle {...fillSoft} cx="22" cy="18" r="3" />
      <path {...sw} d="M44 6c-6 12 6 18 0 30s6 16 0 30M54 6c-6 12 6 18 0 30s6 16 0 30M64 6c-6 12 6 18 0 30" />
      <rect {...fillSoft} x="6" y="48" width="26" height="8" rx="4" /><path {...sw} d="M12 52h14" />
      <Sparkle x="36" y="40" r="4" />
    </>
  ),
  // a key ring with dangling charms
  charms: (
    <>
      <circle {...sw} cx="18" cy="16" r="9" />
      <path {...sw} d="M18 25v8M30 20l8 14M8 24l-3 10" />
      <path {...fillSoft} d="M18 46c-6-6-12 0-8 6l8 8 8-8c4-6-2-12-8-6z" />
      <path {...fillSoft} d="M41 36l3 6 7 1-5 5 1 7-6-3-6 3 1-7-5-5 7-1z" />
      <circle {...fillSoft} cx="6" cy="40" r="4" />
      <path {...sw} d="M54 8h10M59 3v10" opacity="0.8" />
    </>
  ),
  // a hanger, a t-shirt, buttons
  apparel: (
    <>
      <path {...sw} d="M18 12a4 4 0 1 1 4 4v3l22 14a3 3 0 0 1-2 6H-4a3 3 0 0 1-2-6l22-14" transform="translate(6 -2) scale(.8)" />
      <path {...fillSoft} d="M42 14l8-4 8 4 8 4-4 10-6-3v26H44V25l-6 3-4-10z" />
      <circle {...sw} cx="12" cy="58" r="2.4" /><circle {...sw} cx="22" cy="58" r="2.4" />
      <path {...sw} d="M8 46h20" strokeDasharray="2 3" />
    </>
  ),
  // a tote with handles, a small purse
  bags: (
    <>
      <path {...fillSoft} d="M8 30h32l3 26H5z" />
      <path {...sw} d="M16 30v-6a8 8 0 0 1 16 0v6" />
      <path {...fillSoft} d="M48 8h18v14H48z" /><path {...sw} d="M52 8V5a5 5 0 0 1 10 0v3M48 14h18" />
      <circle {...sw} cx="57" cy="14" r="1.6" />
      <path {...sw} d="M52 52h14M52 59h10" opacity="0.8" /><Sparkle x="62" y="40" r="3" />
    </>
  ),
  // a postage stamp, a postcard, a star
  collectibles: (
    <>
      <path {...fillSoft} d="M6 8h4v-3h4v3h4v-3h4v3h4v-3h4v3h3v4h-3v4h3v4h-3v4h3v4h-3v4H6v-4h3v-4H6v-4h3v-4H6v-4h3V8z" />
      <path {...sw} d="M12 22l7-8 5 6 3-3 5 5" />
      <rect {...fillSoft} x="38" y="40" width="28" height="20" rx="2" /><path {...sw} d="M38 42l14 10 14-10M44 56l5-4M60 56l-5-4" />
      <path {...fillSoft} d="M58 10l2.5 5 5.5.8-4 4 1 5.5-5-2.7-5 2.7 1-5.5-4-4 5.5-.8z" />
    </>
  ),
  // a potted plant, a table lamp, a picture frame
  home: (
    <>
      <path {...fillSoft} d="M8 66V52h20v14z" />
      <path {...sw} d="M18 52V34M18 42c-8 0-12-6-12-12 8 0 12 4 12 12zM18 38c8 0 12-6 12-12-8 0-12 4-12 12z" />
      <path {...fillSoft} d="M46 20h18l-3 14H49z" /><path {...sw} d="M55 34v18M48 52h14M55 20V10" />
      <rect {...sw} x="40" y="56" width="20" height="14" rx="1" opacity="0.9" />
      <circle {...fillSoft} cx="12" cy="12" r="5" />
    </>
  ),
  // a lipstick, a compact mirror, brush strokes
  beauty: (
    <>
      <rect {...fillSoft} x="8" y="30" width="12" height="30" rx="2" /><path {...fillSoft} d="M9 30V18l11-6v18z" />
      <circle {...fillSoft} cx="50" cy="22" r="13" /><circle {...sw} cx="50" cy="22" r="8" />
      <path {...sw} d="M48 15c4 0 7 3 7 7" opacity="0.8" />
      <path {...sw} d="M32 62c8-10 16-2 24-10s8-10 10-12" />
      <path {...fillSoft} d="M62 56c3 5 5 8 0 12-5-4-3-7 0-12z" />
    </>
  ),
  // a gift box with a ribbon and bow, confetti
  bundles: (
    <>
      <rect {...fillSoft} x="8" y="34" width="34" height="26" rx="2" /><rect {...fillSoft} x="5" y="26" width="40" height="9" rx="2" />
      <path {...sw} d="M25 26v34" /><path {...sw} d="M25 26c-8-10-16-2-8 0M25 26c8-10 16-2 8 0" />
      <path {...fillSoft} d="M54 12l8 4-4 8-8-4z" /><circle {...sw} cx="56" cy="46" r="3" /><path {...sw} d="M48 56l6 6M64 34h6" />
      <Sparkle x="58" y="30" r="3" />
    </>
  ),
};

export function CategoryArt({ slug, style }) {
  const art = ART[slug];
  const hue = CATEGORY_HUES[slug] || { ink: "#6B645B" };
  if (!art) return null;
  return (
    <svg viewBox="0 0 72 72" preserveAspectRatio="xMidYMid slice" aria-hidden="true" focusable="false"
      style={{ position: "absolute", inset: 0, width: "100%", height: "100%", color: hue.ink, opacity: 0.55, pointerEvents: "none", ...style }}>
      {art}
    </svg>
  );
}

// The finished tile: a flat sand square with the category's line icon (the illustrations above are no longer drawn — the look is
// minimal now, one tone for every category).
export function CategoryArtTile({ slug, icon, iconSize = 28, strokeWidth = 1.25, style }) {
  return (
    <span style={{ position: "relative", overflow: "hidden", display: "flex", alignItems: "center", justifyContent: "center", background: "var(--surface)", color: "var(--ink)", ...style }}>
      <span style={{ position: "relative", display: "flex" }}><Icon n={icon} size={iconSize} strokeWidth={strokeWidth} /></span>
    </span>
  );
}
